import { useAppSelector, useAppDispatch } from "app/hooks";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Image from "react-bootstrap/Image";
import Form from "react-bootstrap/Form";
import {
  changeLayout,
  changeStream,
  changeWaitingScreen,
  changeSetVolume,
  selectLayout,
  selectStreamSlots,
  selectWaitingScreen,
  changeDesiredVolume,
} from "./layoutSlice";
import { LayoutPublisher } from "./layoutPublisher";
import { useGetLayoutStatusQuery } from "api/singularApiSlice";
import { camList } from "app/streamData";
import layouts from "./layouts";

export function LayoutChooser() {
  const currentLayoutName = useAppSelector(selectLayout);
  const streamSlots = useAppSelector(selectStreamSlots);
  const waitingScreen = useAppSelector(selectWaitingScreen);
  const dispatch = useAppDispatch();

  const { data: payloadData = [], isSuccess } = useGetLayoutStatusQuery(null, {
    pollingInterval: 10000,
  });

  if (isSuccess) {
    // Update State with new data
    if (streamSlots[0] === null) {
      try {
        var fullscreen = payloadData.find(
          (layout) => layout.compositionName === "1X"
        ) as any;
        var pip = payloadData.find(
          (layout) => layout.compositionName === "PIP"
        ) as any;
        var triple = payloadData.find(
          (layout) => layout.compositionName === "1+2"
        ) as any;
        var quad = payloadData.find(
          (layout) => layout.compositionName === "2X2"
        ) as any;
        var newLayout = "";
        var newStreams: Array<string | null> = [null, null, null, null];
        var volume = 100;


        //------------------------------------------------------
        // QUAD BOX
        //------------------------------------------------------
        if (quad.animation.state === "In") {
          var slot0 = camList.find(
            (cam) => cam.streamUrl === quad.controlNode.payload["CAM 1"]
          );
          var slot1 = camList.find(
            (cam) => cam.streamUrl === quad.controlNode.payload["CAM 2"]
          );
          var slot2 = camList.find(
            (cam) => cam.streamUrl === quad.controlNode.payload["CAM 3"]
          );
          var slot3 = camList.find(
            (cam) => cam.streamUrl === quad.controlNode.payload["CAM 4"]
          );
          // Cast to number because the volume is a string or default to 100
          volume =   Number(quad.controlNode.payload["CAM 1 Volume"]) || 100;

          newLayout = "Quadbox";
          newStreams = [
            slot0 ? slot0.name : null,
            slot1 ? slot1.name : null,
            slot2 ? slot2.name : null,
            slot3 ? slot3.name : null,
          ];
        //------------------------------------------------------
        // TRIPLE BOX
        //------------------------------------------------------
        } else if (triple.animation.state === "In") {
          slot0 = camList.find(
            (cam) => cam.streamUrl === triple.controlNode.payload["2+1 CAM 1"]
          );
          slot1 = camList.find(
            (cam) => cam.streamUrl === triple.controlNode.payload["2+1 CAM 2"]
          );
          slot2 = camList.find(
            (cam) => cam.streamUrl === triple.controlNode.payload["2+1 CAM 3"]
          );
          volume =   Number(triple.controlNode.payload["2+1 CAM 1 Volume"]) || 100;

          newLayout = "Triple";
          newStreams = [
            slot0 ? slot0.name : null,
            slot1 ? slot1.name : null,
            slot2 ? slot2.name : null,
            null,
          ];
        //------------------------------------------------------
        // PIP
        //------------------------------------------------------
        } else if (pip.animation.state === "In") {
          slot0 = camList.find(
            (cam) =>
              cam.streamUrl === fullscreen.controlNode.payload["1X CAM 1"]
          );
          slot1 = camList.find(
            (cam) => cam.streamUrl === pip.controlNode.payload["PIP"]
          );
          volume =   Number(fullscreen.controlNode.payload["1X Volume"]) || 100;
          newLayout = "Picture in Picture";
          newStreams = [
            slot0 ? slot0.name : null,
            slot1 ? slot1.name : null,
            null,
            null,
          ];
        //------------------------------------------------------
        // SOLO CAM
        //------------------------------------------------------
        } else {
          slot0 = camList.find(
            (cam) =>
              cam.streamUrl === fullscreen.controlNode.payload["1X CAM 1"]
          );
          newLayout = "Fullscreen";
          newStreams = [slot0 ? slot0.name : null, null, null, null];
          volume =   Number(fullscreen.controlNode.payload["1X Volume"]) || 69;
        }

        dispatch(changeDesiredVolume(volume));
        dispatch(changeSetVolume(volume));
        dispatch(changeLayout(newLayout));
        dispatch(changeStream({ slot: 0, stream: newStreams[0] }));
        dispatch(changeStream({ slot: 1, stream: newStreams[1] }));
        dispatch(changeStream({ slot: 2, stream: newStreams[2] }));
        dispatch(changeStream({ slot: 3, stream: newStreams[3] }));
      } catch (e) {
        dispatch(changeLayout("Fullscreen"));
        dispatch(changeStream({ slot: 0, stream: camList[0].name }));
        console.log("Error parsing SL state");
        console.log(e);
      }
    }
    try {
      var newWaitingScreenObj = payloadData.find(
        (layout) => layout.compositionName === "Waiting Screen"
      ) as any;
      var newWaitingScreen = newWaitingScreenObj.animation.state === "In";
      if (newWaitingScreen !== waitingScreen)
        dispatch(changeWaitingScreen(newWaitingScreen));
    } catch (e) {
      console.log("Error parsing Waiting state");
      console.log(e);
    }
  }

  const SelectedLayoutView = layouts.find(
    (layout) => layout.layoutDetails.name === currentLayoutName
  ) ?? layouts[0];

  const renderedList = layouts.map(({layoutDetails}) => (
    <Col
      key={layoutDetails.name}
      className={`my-2 camera-layout${
        layoutDetails.name === currentLayoutName ? " selected" : ""
      }`}
      onClick={() => dispatch(changeLayout(layoutDetails.name))}
      xxl={12}
    >
      <Row>
        <Col className="d-flex align-items-center" xs={2}>
          <div className="ms-auto">
            <Form.Check.Input
              checked={layoutDetails.name === currentLayoutName}
              type={"checkbox"}
              isValid
            />
          </div>
        </Col>
        <Col>
          <Image fluid src={layoutDetails.image} />
        </Col>
      </Row>
    </Col>
  ));

  return (
    <Row>
      <Col className="text-center order-lg-2">
        {<SelectedLayoutView.view/>}
        <LayoutPublisher/>
      </Col>
      <Col lg={3} className="order-lg-1">
        <Row>{renderedList}</Row>
      </Col>
    </Row>
  );
}
